import bcrypt from 'bcryptjs';

/**
 * Password validation requirements
 */
export const PASSWORD_REQUIREMENTS = {
  minLength: 8,
  requireUppercase: true,
  requireLowercase: true,
  requireNumber: true,
  requireSpecial: true,
};

/**
 * Validates password meets strong password requirements
 * @param password The password to validate
 * @returns Object with isValid boolean and error messages array
 */
export function validatePassword(password: string): { isValid: boolean; errors: string[] } {
  const errors: string[] = [];
  
  if (password.length < PASSWORD_REQUIREMENTS.minLength) {
    errors.push(`Password must be at least ${PASSWORD_REQUIREMENTS.minLength} characters`);
  }
  
  if (PASSWORD_REQUIREMENTS.requireUppercase && !/[A-Z]/.test(password)) {
    errors.push('Password must contain at least one uppercase letter');
  }
  
  if (PASSWORD_REQUIREMENTS.requireLowercase && !/[a-z]/.test(password)) {
    errors.push('Password must contain at least one lowercase letter');
  }
  
  if (PASSWORD_REQUIREMENTS.requireNumber && !/[0-9]/.test(password)) {
    errors.push('Password must contain at least one number');
  }
  
  if (PASSWORD_REQUIREMENTS.requireSpecial && !/[!@#$%^&*]/.test(password)) {
    errors.push('Password must contain at least one special character (!@#$%^&*)');
  }
  
  return {
    isValid: errors.length === 0,
    errors,
  };
}

/**
 * Generates a strong password with uppercase, lowercase, numbers, and special characters
 * @param length Password length (minimum 8)
 * @returns A secure password string
 */
export function generateStrongPassword(length: number = 12): string {
  const minLength = 8;
  const actualLength = Math.max(length, minLength);
  
  const uppercase = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
  const lowercase = 'abcdefghjkmnpqrstuvwxyz';
  const numbers = '23456789';
  const special = '!@#$%^&*';
  
  // Ensure at least one of each required type
  const required = [
    uppercase[Math.floor(Math.random() * uppercase.length)],
    lowercase[Math.floor(Math.random() * lowercase.length)],
    numbers[Math.floor(Math.random() * numbers.length)],
    special[Math.floor(Math.random() * special.length)],
  ];
  
  // Fill remaining with random chars from all sets
  const allChars = uppercase + lowercase + numbers + special;
  const remaining = actualLength - required.length;
  
  for (let i = 0; i < remaining; i++) {
    required.push(allChars[Math.floor(Math.random() * allChars.length)]);
  }
  
  // Shuffle the password
  for (let i = required.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [required[i], required[j]] = [required[j], required[i]];
  }
  
  return required.join('');
}

/**
 * Generates a cryptographically secure meeting code
 * @returns A 12-character uppercase alphanumeric code in format XXXX-XXXX-XXXX
 */
export function generateSecureMeetingCode(): string {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  const segments = [];
  
  for (let i = 0; i < 3; i++) {
    let segment = '';
    for (let j = 0; j < 4; j++) {
      const array = new Uint8Array(1);
      crypto.getRandomValues(array);
      segment += chars[array[0] % chars.length];
    }
    segments.push(segment);
  }
  
  return segments.join('-');
}

/**
 * Hashes a password using bcrypt
 * @param password The plaintext password
 * @returns The hashed password
 */
export async function hashPassword(password: string): Promise<string> {
  if (!password || password.length === 0) {
    throw new Error('Password cannot be empty');
  }
  
  if (password.length < 4) {
    throw new Error('Password must be at least 4 characters long');
  }
  
  const saltRounds = 10;
  return await bcrypt.hash(password, saltRounds);
}

/**
 * Verifies a password against a hash
 * @param password The plaintext password
 * @param hash The hashed password
 * @returns True if the password matches the hash
 */
export async function verifyPassword(password: string, hash: string): Promise<boolean> {
  return await bcrypt.compare(password, hash);
}
